<script lang="ts">
	import { buildPageSeo, buildPersonSchema, renderJsonLd } from '$lib/seo';
	import { absoluteUrl, site } from '$lib/site';

	const description =
		'About Jarle Mathiesen, software engineer writing about AI, systems, and the craft of building good products.';
	const seo = buildPageSeo({
		title: 'About',
		description,
		path: '/about',
		keywords: ['Jarle Mathiesen', 'software engineer', 'AI', 'product development']
	});
	const personSchema = renderJsonLd(buildPersonSchema('/about', description));
</script>

<svelte:head>
	<title>{seo.title}</title>
	<meta name="description" content={seo.description} />
	<meta name="author" content={site.author} />
	<meta name="keywords" content={seo.keywords} />
	<link rel="canonical" href={seo.canonical} />
	<link rel="alternate" type="application/rss+xml" title={`${site.name} RSS`} href={absoluteUrl('/rss.xml')} />
	<meta property="og:type" content={seo.type} />
	<meta property="og:title" content={seo.title} />
	<meta property="og:description" content={seo.description} />
	<meta property="og:url" content={seo.canonical} />
	<meta property="og:image" content={seo.image} />
	<meta property="og:site_name" content={site.name} />
	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:title" content={seo.title} />
	<meta name="twitter:description" content={seo.description} />
	<meta name="twitter:image" content={seo.image} />
	{@html personSchema}
</svelte:head>

<article class="page-shell about-page">
	<header class="page-header">
		<div class="page-header__top">
			<a class="header-link" href="/">Writing</a>
		</div>
		<h1>About</h1>
		<p class="intro">
			I’m Jarle Mathiesen, a software engineer writing about AI, systems, and the craft of
			building good products.
		</p>
	</header>

	<section class="about-hero" aria-label="About Jarle Mathiesen">
		<img
			class="about-hero__image"
			src={site.portraitImage}
			alt="Portrait of Jarle Mathiesen"
			loading="eager"
			decoding="async"
		/>
		<div class="about-hero__body">
			<p>
				I work at the intersection of software engineering, product development, and AI. I care
				most about clear systems, durable judgment, and products that feel considered in the
				right places.
			</p>
			<p>
				This site is where I publish essays and notes on engineering, AI, and how teams build
				well when the tools and constraints keep changing.
			</p>
		</div>
	</section>

	<section class="about-section">
		<p class="section-label">What I Care About</p>
		<div class="about-section__body">
			<p>Software that stays understandable as it grows.</p>
			<p>AI used with judgment rather than novelty for its own sake.</p>
			<p>Products with technical depth and editorial quality.</p>
		</div>
	</section>

	<section class="about-section">
		<p class="section-label">Why This Exists</p>
		<div class="about-section__body">
			<p>
				I want this to be a durable home for my writing and point of view. If someone arrives
				here, they should quickly understand what I think about, what I care about, and what
				kind of work I respect.
			</p>
		</div>
	</section>

	<footer class="site-footer">
		<a href="/projects">Projects</a>
		<a href="/guides">Guides</a>
		<a href="/">Back</a>
		<a href="/rss.xml">RSS</a>
	</footer>
</article>

<style>
	.page-shell {
		width: min(var(--measure-page), calc(100% - var(--space-2xl)));
		margin: 0 auto;
		min-height: 100svh;
		padding: calc(var(--space-4xl) + var(--space-sm)) 0 var(--space-5xl);
	}

	.page-header {
		display: grid;
		gap: var(--space-md);
		padding-bottom: calc(var(--space-3xl) + var(--space-sm));
	}

	.header-link {
		font-family: var(--font-mono);
		font-size: 0.82rem;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-soft);
	}

	.intro {
		max-width: var(--measure-intro-wide);
		font-size: 1.04rem;
		line-height: 1.7;
		color: var(--color-muted);
	}

	.about-page {
		display: flex;
		flex-direction: column;
		gap: var(--space-3xl);
	}

	.about-hero {
		display: grid;
		grid-template-columns: 11rem minmax(0, 1fr);
		gap: var(--space-2xl);
		align-items: start;
	}

	.about-hero__image {
		width: 11rem;
		height: 11rem;
		border-radius: 50%;
		object-fit: cover;
	}

	.about-hero__body {
		display: grid;
		gap: var(--space-lg);
		max-width: var(--measure-prose);
		font-family: var(--font-prose);
		font-size: 1.16rem;
		line-height: 1.82;
		color: var(--color-ink-2);
	}

	.section-label {
		font-family: var(--font-mono);
		font-size: var(--text-label);
		font-weight: 600;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-soft);
	}

	.about-section {
		display: grid;
		grid-template-columns: minmax(0, 11rem) minmax(0, 1fr);
		gap: var(--space-2xl);
	}

	.about-section__body {
		display: grid;
		gap: var(--space-lg);
		max-width: var(--measure-prose);
		font-family: var(--font-prose);
		font-size: 1.12rem;
		line-height: 1.82;
		color: var(--color-ink-2);
	}

	.site-footer {
		display: flex;
		gap: var(--space-lg);
		margin-top: auto;
		padding-top: var(--space-2xl);
		font-family: var(--font-mono);
		font-size: 0.82rem;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-muted);
	}

	@media (max-width: 640px) {
		.page-shell {
			padding-top: var(--space-xl);
			padding-bottom: var(--space-4xl);
		}

		.about-hero,
		.about-section {
			grid-template-columns: 1fr;
			gap: var(--space-lg);
		}

		.about-hero__image {
			width: 8rem;
			height: 8rem;
		}

		.about-hero__body,
		.about-section__body {
			font-size: 1.04rem;
			line-height: 1.74;
		}
	}
</style>
